import { AuthError } from '@classgo/shared/auth';
import { Button, Input } from '@classgo/ui';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { useCognito } from '../../auth/auth-provider';
import { useKitT } from '../../i18n';
import { AuthLayout, FormError } from '../auth-layout';

type Step = 'request' | 'confirm' | 'done';

/**
 * Password recovery: the email gets a code, the code and a new password finish it. The screen
 * never says whether the email exists.
 */
export const ForgotPasswordScreen = () => {
  const t = useKitT();
  const cognito = useCognito();
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const attempt = async (action: () => Promise<void>, next: Step) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setStep(next);
    } catch (thrown) {
      if (thrown instanceof AuthError && thrown.code === 'USER_NOT_FOUND' && step === 'request') {
        setStep(next);
      } else {
        setError(
          thrown instanceof AuthError ? t(`auth.error.${thrown.code}`) : t('auth.error.UNKNOWN'),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (step === 'request') {
      void attempt(() => cognito.forgotPassword(normalized), 'confirm');
    } else if (step === 'confirm') {
      void attempt(() => cognito.confirmForgotPassword(normalized, code.trim(), password), 'done');
    }
  };

  return (
    <AuthLayout
      title={t('forgot.title')}
      {...(step === 'request' ? { subtitle: t('forgot.help') } : {})}
      footer={
        <Link
          to="/login"
          search={{ redirect: undefined }}
          className="text-brand-600 underline underline-offset-2 hover:decoration-2"
        >
          {t('forgot.backToLogin')}
        </Link>
      }
    >
      {step === 'done' ? (
        <p role="status" className="text-sm text-gray-700">
          {t('forgot.done')}
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          <FormError message={error} />
          {step === 'confirm' ? (
            <p
              role="status"
              className="rounded-control bg-info-100 px-3 py-2 text-sm text-info-700"
            >
              {t('forgot.sent')}
            </p>
          ) : null}
          <Input
            label={t('login.email')}
            type="email"
            autoComplete="email"
            required
            readOnly={step === 'confirm'}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {step === 'confirm' ? (
            <>
              <Input
                label={t('forgot.code')}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
              <Input
                label={t('forgot.newPassword')}
                type="password"
                autoComplete="new-password"
                required
                help={t('password.policy')}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </>
          ) : null}
          <Button type="submit" loading={busy} className="w-full">
            {step === 'request' ? t('forgot.submit') : t('forgot.confirm')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
};
